import React, { useState } from 'react';
import {
  Settings,
  Shield,
  Image,
  Video,
  Bell,
  Globe,
  Trash2,
  Save,
  ToggleLeft,
  ToggleRight,
  AlertTriangle,
  CheckCircle2,
  Sliders
} from 'lucide-react';

const SettingsSection = ({ title, icon: Icon, children }) => (
  <div className="glass-panel admin-card rounded-3xl p-6 space-y-5">
    <div className="flex items-center gap-3 pb-4 border-b border-white/10">
      <div className="w-9 h-9 rounded-xl bg-violet-500/15 border border-violet-500/25 flex items-center justify-center">
        <Icon size={18} className="text-violet-400" />
      </div>
      <h2 className="text-lg font-bold text-white">{title}</h2>
    </div>
    {children}
  </div>
);

const ToggleSetting = ({ label, description, value, onChange }) => (
  <div className="flex items-center justify-between gap-4">
    <div>
      <p className="text-sm font-semibold text-white">{label}</p>
      {description && <p className="text-xs text-white/40 mt-0.5">{description}</p>}
    </div>
    <button
      onClick={() => onChange(!value)}
      className="flex-shrink-0 transition-all duration-200"
    >
      {value
        ? <ToggleRight size={36} className="text-violet-400" />
        : <ToggleLeft size={36} className="text-white/30" />}
    </button>
  </div>
);

const SliderSetting = ({ label, value, min, max, unit, onChange }) => (
  <div className="space-y-2">
    <div className="flex items-center justify-between">
      <p className="text-sm font-semibold text-white">{label}</p>
      <span className="text-xs font-bold text-violet-300 bg-violet-500/15 px-2 py-0.5 rounded-full">
        {value} {unit}
      </span>
    </div>
    <input
      type="range"
      min={min}
      max={max}
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      className="w-full h-1.5 appearance-none rounded-full cursor-pointer"
      style={{
        background: `linear-gradient(to right, #a78bfa ${((value - min) / (max - min)) * 100}%, rgba(255,255,255,0.1) 0%)`
      }}
    />
    <div className="flex justify-between text-[10px] text-white/30">
      <span>{min} {unit}</span>
      <span>{max} {unit}</span>
    </div>
  </div>
);

const AdminSettings = () => {
  // Platform
  const [platformName, setPlatformName] = useState('Aether');
  const [allowRegistrations, setAllowRegistrations] = useState(true);
  const [requireEmailVerification, setRequireEmailVerification] = useState(false);
  const [maintenanceMode, setMaintenanceMode] = useState(false);

  // Media limits
  const [maxPhotoMB, setMaxPhotoMB] = useState(5);
  const [maxVideoMB, setMaxVideoMB] = useState(50);
  const [allowVideos, setAllowVideos] = useState(true);

  // Notifications
  const [emailOnReport, setEmailOnReport] = useState(true);
  const [emailDigest, setEmailDigest] = useState(false);

  // Save state
  const [saved, setSaved] = useState(false);

  const handleSave = () => {
    // Persist to localStorage as a stand-in for a real API call
    const settings = {
      platformName,
      allowRegistrations,
      requireEmailVerification,
      maintenanceMode,
      maxPhotoMB,
      maxVideoMB,
      allowVideos,
      emailOnReport,
      emailDigest
    };
    localStorage.setItem('admin_settings', JSON.stringify(settings));
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const handleClearCache = () => {
    if (!window.confirm('This will clear all locally stored posts and sessions. Continue?')) return;
    localStorage.removeItem('local_posts');
    localStorage.removeItem('liked_posts');
    localStorage.removeItem('mock_profiles');
    alert('Local cache cleared.');
  };

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700 max-w-3xl">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold gradient-text flex items-center gap-3">
            <Sliders size={30} className="text-violet-400" />
            System Settings
          </h1>
          <p className="text-white/40 mt-1 text-sm">Configure platform behaviour and limits.</p>
        </div>
        <button
          onClick={handleSave}
          className={`flex items-center gap-2 px-5 py-2.5 rounded-2xl font-bold text-sm transition-all ${
            saved
              ? 'bg-emerald-500/20 border border-emerald-500/40 text-emerald-300'
              : 'bg-violet-600 hover:bg-violet-500 text-white shadow-lg shadow-violet-900/40'
          }`}
        >
          {saved ? <CheckCircle2 size={16} /> : <Save size={16} />}
          {saved ? 'Saved!' : 'Save Changes'}
        </button>
      </div>

      {/* Platform */}
      <SettingsSection title="Platform" icon={Globe}>
        <div className="space-y-2">
          <label className="text-xs font-bold uppercase tracking-wider text-white/40">Platform Name</label>
          <input
            type="text"
            value={platformName}
            onChange={(e) => setPlatformName(e.target.value)}
            className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-violet-500/60 transition-colors"
          />
        </div>
        <ToggleSetting
          label="Allow New Registrations"
          description="When off, no new accounts can be created."
          value={allowRegistrations}
          onChange={setAllowRegistrations}
        />
        <ToggleSetting
          label="Require Email Verification"
          description="New users must verify their email before posting."
          value={requireEmailVerification}
          onChange={setRequireEmailVerification}
        />
        <ToggleSetting
          label="Maintenance Mode"
          description="Shows a maintenance banner to all users."
          value={maintenanceMode}
          onChange={setMaintenanceMode}
        />
      </SettingsSection>

      {/* Media Limits */}
      <SettingsSection title="Media Limits" icon={Image}>
        <SliderSetting
          label="Max Photo Size"
          value={maxPhotoMB}
          min={1}
          max={20}
          unit="MB"
          onChange={setMaxPhotoMB}
        />
        <ToggleSetting
          label="Allow Video Uploads"
          description="Let users attach videos to posts."
          value={allowVideos}
          onChange={setAllowVideos}
        />
        {allowVideos && (
          <SliderSetting
            label="Max Video Size"
            value={maxVideoMB}
            min={10}
            max={200}
            unit="MB"
            onChange={setMaxVideoMB}
          />
        )}
      </SettingsSection>

      {/* Notifications */}
      <SettingsSection title="Admin Notifications" icon={Bell}>
        <ToggleSetting
          label="Email on New Report"
          description="Receive an email whenever a post is flagged."
          value={emailOnReport}
          onChange={setEmailOnReport}
        />
        <ToggleSetting
          label="Daily Digest Email"
          description="Receive a daily summary of platform activity."
          value={emailDigest}
          onChange={setEmailDigest}
        />
      </SettingsSection>

      {/* Danger Zone */}
      <div className="glass-panel rounded-3xl p-6 border border-rose-500/30 bg-rose-500/5">
        <div className="flex items-center gap-3 pb-4 border-b border-rose-500/20 mb-5">
          <div className="w-9 h-9 rounded-xl bg-rose-500/15 border border-rose-500/25 flex items-center justify-center">
            <AlertTriangle size={18} className="text-rose-400" />
          </div>
          <h2 className="text-lg font-bold text-rose-300">Danger Zone</h2>
        </div>
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="text-sm font-semibold text-white">Clear Local Cache</p>
            <p className="text-xs text-white/40 mt-0.5">
              Removes all locally stored posts, sessions, and mock data. Cannot be undone.
            </p>
          </div>
          <button
            onClick={handleClearCache}
            className="flex-shrink-0 flex items-center gap-2 px-4 py-2 rounded-xl bg-rose-500/15 border border-rose-500/30 text-rose-400 hover:bg-rose-500/25 transition-all text-sm font-bold"
          >
            <Trash2 size={15} />
            Clear
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminSettings;
